import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const category = categories.find((c) => c.slug === slug);
  const categoryLabel = category?.label || '全部漫画';

  const filteredComics = useMemo(() => {
    let result = slug ? comics.filter((c) => c.category === slug) : [...comics];

    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return result;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / perPage);
  const paginatedComics = filteredComics.slice((currentPage - 1) * perPage, currentPage * perPage);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryLabel },
  ];

  const catSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `漫蛙漫画 - ${categoryLabel}`,
    description: `漫蛙漫画网${categoryLabel}专区，海量免费${categoryLabel}资源，高清漫画全集在线阅读。`,
    url: `https://manwacomics.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://manwacomics.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg data={catSchema} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {categoryLabel}专区
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              海量免费{categoryLabel}资源，高清漫画全集在线阅读，持续更新中
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-background-200/70 rounded-full px-1 py-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                className="text-sm border border-background-300 rounded-lg px-3 py-2 bg-background-50 text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
              <p className="mt-4 text-foreground-500">暂无该分类的漫画</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {paginatedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-lg flex items-center justify-center border border-background-200 text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-primary-500 text-white'
                          : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-lg flex items-center justify-center border border-background-200 text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}